import { useState, type FormEvent } from 'react';
import { useParams } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Money } from '@rental/shared';
import { api } from '../lib/api.js';
import { Badge, Button, Card, Empty, Field, Page, Table, TextInput } from '../components/ui.jsx';

interface Tenant {
  id: string;
  fullName: string;
  phone: string | null;
  email: string | null;
  address: string | null;
  isActive: boolean;
}
interface Lease {
  id: string;
  unitId: string;
  tenantId: string;
  startDate: string;
  endDate: string | null;
  monthlyRent: number;
  securityDeposit: number;
  status: 'active' | 'ended' | 'terminated';
}
interface Unit {
  id: string;
  kind: string;
  flatNo: string | null;
  roomNo: string | null;
  monthlyRent: number;
  occupancy: string;
  propertyId: string;
}
interface Property { id: string; name: string; }
interface RentInvoice {
  id: string; leaseId: string; periodYear: number; periodMonth: number;
  amountExpected: number; amountReceived: number;
  status: 'pending' | 'partial' | 'paid' | 'overpaid' | 'waived';
}
type ChargeKind = 'electricity' | 'water' | 'internet' | 'maintenance' | 'other';
interface Charge {
  id: string; leaseId: string; kind: ChargeKind; description: string | null;
  chargedOn: string; periodYear: number | null; periodMonth: number | null;
  amountExpected: number; amountReceived: number;
  status: 'pending' | 'partial' | 'paid' | 'overpaid' | 'waived';
}

export function TenantDetailPage() {
  const { id = '' } = useParams();
  const qc = useQueryClient();

  const tenantQ = useQuery({
    queryKey: ['tenant', id],
    queryFn: () => api<{ tenant: Tenant }>(`/api/v1/tenants/${id}`),
    enabled: !!id,
  });
  const leasesQ = useQuery({
    queryKey: ['tenant', id, 'leases'],
    queryFn: () => api<{ leases: Lease[] }>(`/api/v1/leases?tenantId=${id}`),
    enabled: !!id,
  });
  const propertiesQ = useQuery({
    queryKey: ['properties'],
    queryFn: () => api<{ properties: Property[] }>('/api/v1/properties'),
  });
  const rentQ = useQuery({
    queryKey: ['tenant', id, 'rent'],
    queryFn: () => api<{ invoices: RentInvoice[] }>(`/api/v1/rent/invoices?tenantId=${id}`),
    enabled: !!id,
  });
  const chargesQ = useQuery({
    queryKey: ['tenant', id, 'charges'],
    queryFn: () =>
      api<{ charges: Charge[]; totalExpected: number; totalReceived: number; totalPending: number }>(
        `/api/v1/charges?tenantId=${id}`,
      ),
    enabled: !!id,
  });

  const invalidateLeases = () =>
    void qc.invalidateQueries({ queryKey: ['tenant', id, 'leases'] });
  const invalidateCharges = () => {
    void qc.invalidateQueries({ queryKey: ['tenant', id, 'charges'] });
    void qc.invalidateQueries({ queryKey: ['tenant', id, 'rent'] });
  };

  const pendingRent = (rentQ.data?.invoices ?? [])
    .filter((i) => i.status !== 'waived')
    .reduce((a, i) => a + Math.max(0, i.amountExpected - i.amountReceived), 0);
  const pendingCharges = chargesQ.data?.totalPending ?? 0;
  const totalBalance = pendingRent + pendingCharges;

  if (tenantQ.isLoading) return <Page title="Loading…"><Empty>…</Empty></Page>;
  if (!tenantQ.data) return <Page title="Not found"><Empty>Tenant not found.</Empty></Page>;
  const t = tenantQ.data.tenant;

  return (
    <Page
      title={t.fullName}
      actions={t.isActive ? <Badge tone="good">Active</Badge> : <Badge>Inactive</Badge>}
    >
      <div className="mb-4 grid gap-3 md:grid-cols-3">
        <Card>
          <div className="text-xs text-slate-500">Total balance owed</div>
          <div className={`text-2xl font-semibold ${totalBalance > 0 ? 'text-amber-700 dark:text-amber-400' : 'text-emerald-700 dark:text-emerald-400'}`}>
            {Money.fromPaise(totalBalance).format()}
          </div>
          <div className="mt-1 text-xs text-slate-500">
            Rent pending {Money.fromPaise(pendingRent).format()} · Charges pending {Money.fromPaise(pendingCharges).format()}
          </div>
        </Card>
        <Card className="md:col-span-2">
          <dl className="grid gap-2 text-sm md:grid-cols-3">
            <div><dt className="text-slate-500">Phone</dt><dd>{t.phone ?? '—'}</dd></div>
            <div><dt className="text-slate-500">Email</dt><dd>{t.email ?? '—'}</dd></div>
            <div><dt className="text-slate-500">Address</dt><dd>{t.address ?? '—'}</dd></div>
          </dl>
        </Card>
      </div>

      <section>
        <h2 className="mb-2 text-lg font-medium">Leases</h2>
        <LeaseForm
          tenantId={t.id}
          properties={propertiesQ.data?.properties ?? []}
          onSaved={invalidateLeases}
        />
        <div className="mt-3">
          {!leasesQ.data?.leases.length ? (
            <Empty>No leases yet.</Empty>
          ) : (
            <Table>
              <thead className="text-left text-slate-500">
                <tr>
                  <th className="border-b px-3 py-2 dark:border-slate-700">Status</th>
                  <th className="border-b px-3 py-2 dark:border-slate-700">Unit</th>
                  <th className="border-b px-3 py-2 dark:border-slate-700">From</th>
                  <th className="border-b px-3 py-2 dark:border-slate-700">To</th>
                  <th className="border-b px-3 py-2 dark:border-slate-700">Rent</th>
                  <th className="border-b px-3 py-2 dark:border-slate-700"></th>
                </tr>
              </thead>
              <tbody>
                {leasesQ.data.leases.map((l) => (
                  <LeaseRow key={l.id} lease={l} onChanged={invalidateLeases} />
                ))}
              </tbody>
            </Table>
          )}
        </div>
      </section>

      <section className="mt-6">
        <h2 className="mb-2 text-lg font-medium">Additional charges</h2>
        <p className="mb-2 text-xs text-slate-500">
          Track anything the tenant owes beyond rent — electricity share, water, internet,
          maintenance, or one-off items. Each charge has its own payment ledger and status.
        </p>
        <ChargeForm
          activeLease={(leasesQ.data?.leases ?? []).find((l) => l.status === 'active') ?? null}
          onSaved={invalidateCharges}
        />
        <div className="mt-3">
          {chargesQ.isLoading ? (
            <Empty>Loading…</Empty>
          ) : !chargesQ.data?.charges.length ? (
            <Empty>No additional charges yet.</Empty>
          ) : (
            <Table>
              <thead className="text-left text-slate-500">
                <tr>
                  <th className="border-b px-3 py-2 dark:border-slate-700">Date</th>
                  <th className="border-b px-3 py-2 dark:border-slate-700">Kind</th>
                  <th className="border-b px-3 py-2 dark:border-slate-700">Description</th>
                  <th className="border-b px-3 py-2 dark:border-slate-700 text-right">Amount</th>
                  <th className="border-b px-3 py-2 dark:border-slate-700 text-right">Received</th>
                  <th className="border-b px-3 py-2 dark:border-slate-700 text-right">Pending</th>
                  <th className="border-b px-3 py-2 dark:border-slate-700">Status</th>
                  <th className="border-b px-3 py-2 dark:border-slate-700"></th>
                </tr>
              </thead>
              <tbody>
                {chargesQ.data.charges.map((ch) => (
                  <ChargeRow key={ch.id} charge={ch} onChanged={invalidateCharges} />
                ))}
              </tbody>
            </Table>
          )}
        </div>
      </section>
    </Page>
  );
}

function ChargeRow({ charge, onChanged }: { charge: Charge; onChanged: () => void }) {
  const [open, setOpen] = useState(false);
  const pending = charge.status === 'waived' ? 0 : Math.max(0, charge.amountExpected - charge.amountReceived);
  const record = useMutation({
    mutationFn: (body: Record<string, unknown>) =>
      api(`/api/v1/charges/${charge.id}/payments`, { method: 'POST', body }),
    onSuccess: () => { setOpen(false); onChanged(); },
  });
  const waive = useMutation({
    mutationFn: () => api(`/api/v1/charges/${charge.id}`, { method: 'PATCH', body: { status: 'waived' } }),
    onSuccess: onChanged,
  });
  return (
    <>
      <tr className="border-b border-slate-100 dark:border-slate-800">
        <td className="px-3 py-2 whitespace-nowrap text-slate-500">{charge.chargedOn}</td>
        <td className="px-3 py-2"><Badge tone="neutral">{charge.kind}</Badge></td>
        <td className="px-3 py-2">{charge.description ?? '—'}</td>
        <td className="px-3 py-2 text-right">{Money.fromPaise(charge.amountExpected).format()}</td>
        <td className="px-3 py-2 text-right">{Money.fromPaise(charge.amountReceived).format()}</td>
        <td className="px-3 py-2 text-right">
          <span className={pending > 0 ? 'text-amber-700 dark:text-amber-400' : ''}>
            {Money.fromPaise(pending).format()}
          </span>
        </td>
        <td className="px-3 py-2">
          <Badge tone={
            charge.status === 'paid' || charge.status === 'overpaid' ? 'good'
            : charge.status === 'partial' ? 'warn'
            : charge.status === 'waived' ? 'neutral'
            : 'bad'
          }>{charge.status}</Badge>
        </td>
        <td className="px-3 py-2 text-right">
          <div className="flex justify-end gap-2">
            {pending > 0 && (
              <Button variant="secondary" onClick={() => setOpen((v) => !v)}>
                {open ? 'Close' : 'Record'}
              </Button>
            )}
            {charge.status !== 'waived' && charge.status !== 'paid' && (
              <Button variant="secondary" disabled={waive.isPending} onClick={() => waive.mutate()}>
                Waive
              </Button>
            )}
          </div>
        </td>
      </tr>
      {open && (
        <tr>
          <td colSpan={8} className="bg-slate-50 px-3 py-4 dark:bg-slate-900">
            <ChargePaymentForm suggestedAmount={pending} onSubmit={(body) => record.mutate(body)} pending={record.isPending} />
          </td>
        </tr>
      )}
    </>
  );
}

function ChargePaymentForm({
  suggestedAmount, onSubmit, pending,
}: {
  suggestedAmount: number;
  onSubmit: (body: Record<string, unknown>) => void;
  pending: boolean;
}) {
  const [amount, setAmount] = useState(
    suggestedAmount > 0 ? Money.fromPaise(suggestedAmount).toRupees().toString() : '',
  );
  const [paidOn, setPaidOn] = useState(new Date().toISOString().slice(0, 10));
  const [method, setMethod] = useState<'cash' | 'bank' | 'upi' | 'cheque' | 'other'>('upi');
  const [reference, setReference] = useState('');
  const [error, setError] = useState<string | null>(null);
  function handle(e: FormEvent) {
    e.preventDefault();
    setError(null);
    try {
      onSubmit({
        amount: Money.parse(amount).toPaise(),
        paidOn, method,
        reference: reference || undefined,
      });
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Invalid amount');
    }
  }
  return (
    <form onSubmit={handle} className="grid gap-3 md:grid-cols-5 md:items-end">
      <Field label="Amount"><TextInput required value={amount} onChange={(e) => setAmount(e.target.value)} /></Field>
      <Field label="Paid on"><TextInput type="date" required value={paidOn} onChange={(e) => setPaidOn(e.target.value)} /></Field>
      <Field label="Method">
        <select value={method} onChange={(e) => setMethod(e.target.value as typeof method)}
          className="w-full rounded border border-slate-300 bg-white px-3 py-2 dark:border-slate-600 dark:bg-slate-800">
          <option value="cash">Cash</option><option value="bank">Bank</option>
          <option value="upi">UPI</option><option value="cheque">Cheque</option>
          <option value="other">Other</option>
        </select>
      </Field>
      <Field label="Reference"><TextInput value={reference} onChange={(e) => setReference(e.target.value)} /></Field>
      <div className="flex items-center justify-end gap-2">
        {error && <span className="text-xs text-red-600">{error}</span>}
        <Button type="submit" disabled={pending}>{pending ? 'Saving…' : 'Record'}</Button>
      </div>
    </form>
  );
}

function ChargeForm({ activeLease, onSaved }: { activeLease: Lease | null; onSaved: () => void }) {
  const [kind, setKind] = useState<ChargeKind>('electricity');
  const [amount, setAmount] = useState('');
  const [description, setDescription] = useState('');
  const [chargedOn, setChargedOn] = useState(new Date().toISOString().slice(0, 10));
  const [error, setError] = useState<string | null>(null);
  const mutation = useMutation({
    mutationFn: (body: Record<string, unknown>) => api('/api/v1/charges', { method: 'POST', body }),
    onSuccess: () => { setAmount(''); setDescription(''); onSaved(); },
    onError: (e) => setError(e instanceof Error ? e.message : 'Save failed'),
  });

  if (!activeLease) {
    return (
      <Card>
        <p className="text-sm text-slate-500">
          Start an active lease first — additional charges attach to a lease.
        </p>
      </Card>
    );
  }
  function handle(e: FormEvent) {
    e.preventDefault();
    setError(null);
    try {
      mutation.mutate({
        leaseId: activeLease!.id,
        kind,
        amount: Money.parse(amount).toPaise(),
        chargedOn,
        description: description || undefined,
      });
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Invalid amount');
    }
  }
  return (
    <Card>
      <form onSubmit={handle} className="grid gap-3 md:grid-cols-5 md:items-end">
        <Field label="Kind">
          <select value={kind} onChange={(e) => setKind(e.target.value as ChargeKind)}
            className="w-full rounded border border-slate-300 bg-white px-3 py-2 dark:border-slate-600 dark:bg-slate-800">
            <option value="electricity">Electricity</option>
            <option value="water">Water</option>
            <option value="internet">Internet</option>
            <option value="maintenance">Maintenance</option>
            <option value="other">Other</option>
          </select>
        </Field>
        <Field label="Amount (₹)"><TextInput required value={amount} onChange={(e) => setAmount(e.target.value)} placeholder="1500" /></Field>
        <Field label="Charged on"><TextInput type="date" required value={chargedOn} onChange={(e) => setChargedOn(e.target.value)} /></Field>
        <div className="md:col-span-2">
          <Field label="Description">
            <TextInput value={description} onChange={(e) => setDescription(e.target.value)} placeholder="e.g. August share of electricity bill" />
          </Field>
        </div>
        <div className="md:col-span-5 flex items-center justify-end gap-2">
          {error && <span className="text-xs text-red-600">{error}</span>}
          <Button type="submit" disabled={mutation.isPending}>{mutation.isPending ? 'Saving…' : '+ Add charge'}</Button>
        </div>
      </form>
    </Card>
  );
}

function LeaseRow({ lease, onChanged }: { lease: Lease; onChanged: () => void }) {
  const endLease = useMutation({
    mutationFn: (body: { endDate: string; status: 'ended' | 'terminated' }) =>
      api(`/api/v1/leases/${lease.id}/end`, { method: 'POST', body }),
    onSuccess: onChanged,
  });
  const today = new Date().toISOString().slice(0, 10);
  return (
    <tr className="border-b border-slate-100 dark:border-slate-800">
      <td className="px-3 py-2">
        {lease.status === 'active'
          ? <Badge tone="good">Active</Badge>
          : lease.status === 'ended'
          ? <Badge>Ended</Badge>
          : <Badge tone="warn">Terminated</Badge>}
      </td>
      <td className="px-3 py-2 text-slate-500">{lease.unitId.slice(-6)}</td>
      <td className="px-3 py-2">{lease.startDate}</td>
      <td className="px-3 py-2">{lease.endDate ?? '—'}</td>
      <td className="px-3 py-2">{Money.fromPaise(lease.monthlyRent).format()}</td>
      <td className="px-3 py-2 text-right">
        {lease.status === 'active' && (
          <Button
            variant="secondary"
            disabled={endLease.isPending}
            onClick={() =>
              endLease.mutate({ endDate: today, status: 'ended' })
            }
          >
            End today
          </Button>
        )}
      </td>
    </tr>
  );
}

function LeaseForm({
  tenantId, properties, onSaved,
}: { tenantId: string; properties: Property[]; onSaved: () => void }) {
  const [propertyId, setPropertyId] = useState('');
  const [unitId, setUnitId] = useState('');
  const [startDate, setStartDate] = useState(new Date().toISOString().slice(0, 10));
  const [rent, setRent] = useState('');
  const [deposit, setDeposit] = useState('');
  const [dueDay, setDueDay] = useState('5');
  const [error, setError] = useState<string | null>(null);

  const unitsQ = useQuery({
    queryKey: ['property', propertyId, 'units'],
    queryFn: () => api<{ units: Unit[] }>(`/api/v1/properties/${propertyId}/units`),
    enabled: !!propertyId,
  });

  const mutation = useMutation({
    mutationFn: (body: Record<string, unknown>) =>
      api('/api/v1/leases', { method: 'POST', body }),
    onSuccess: () => {
      setUnitId(''); setRent(''); setDeposit('');
      onSaved();
    },
    onError: (e) => setError(e instanceof Error ? e.message : 'Save failed'),
  });

  function onSubmit(e: FormEvent) {
    e.preventDefault();
    setError(null);
    try {
      const rentPaise = Money.parse(rent).toPaise();
      const depositPaise = deposit ? Money.parse(deposit).toPaise() : 0;
      mutation.mutate({
        tenantId,
        unitId,
        startDate,
        monthlyRent: rentPaise,
        securityDeposit: depositPaise,
        rentDueDay: Number(dueDay),
      });
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Invalid amount');
    }
  }

  return (
    <Card>
      <form onSubmit={onSubmit} className="grid gap-3 md:grid-cols-6 md:items-end">
        <Field label="Property">
          <select
            required value={propertyId} onChange={(e) => { setPropertyId(e.target.value); setUnitId(''); }}
            className="w-full rounded border border-slate-300 bg-white px-3 py-2 dark:border-slate-600 dark:bg-slate-800"
          >
            <option value="">— select —</option>
            {properties.map((p) => (<option key={p.id} value={p.id}>{p.name}</option>))}
          </select>
        </Field>
        <Field label="Unit">
          <select
            required value={unitId} onChange={(e) => setUnitId(e.target.value)} disabled={!propertyId}
            className="w-full rounded border border-slate-300 bg-white px-3 py-2 dark:border-slate-600 dark:bg-slate-800"
          >
            <option value="">— select —</option>
            {(unitsQ.data?.units ?? []).map((u) => (
              <option key={u.id} value={u.id}>
                {[u.kind, u.flatNo, u.roomNo].filter(Boolean).join(' ')} ({u.occupancy})
              </option>
            ))}
          </select>
        </Field>
        <Field label="Start date">
          <TextInput type="date" required value={startDate} onChange={(e) => setStartDate(e.target.value)} />
        </Field>
        <Field label="Rent (₹/month)">
          <TextInput required value={rent} onChange={(e) => setRent(e.target.value)} placeholder="15000" />
        </Field>
        <Field label="Deposit (₹)">
          <TextInput value={deposit} onChange={(e) => setDeposit(e.target.value)} placeholder="30000" />
        </Field>
        <Field label="Rent due day" hint="1–28">
          <TextInput type="number" min={1} max={28} value={dueDay} onChange={(e) => setDueDay(e.target.value)} />
        </Field>
        <div className="md:col-span-6 flex items-center justify-end gap-2">
          {error && <span className="text-xs text-red-600">{error}</span>}
          <Button type="submit" disabled={mutation.isPending}>Start lease</Button>
        </div>
      </form>
    </Card>
  );
}
